import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { isErrorWithData } from '@/services/helpers';
import { useCreateProductLineMutation } from '@/services/productLines';
import { PalmettoButton, PalmettoTextInput } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { ErrorCode } from 'types';

import { ProductLineBreadcrumb, productLinesPath } from './ProductLineBreadcrumb';
import { atLeastOneValueMessage, cleanProductLineValues, ProductLineValuesEditor } from './ProductLineValuesEditor';

interface ProductLineFormValues {
  name: string;
  values: string[];
}

const emptyValues: ProductLineFormValues = { name: '', values: [''] };

/** Create only: a line's name never changes, and its values are edited from the detail page. */
export const ProductLineForm = () => {
  const navigate = useNavigate();
  const [createProductLine, { isLoading }] = useCreateProductLineMutation();

  const { control, handleSubmit, setError } = useForm<ProductLineFormValues>({ defaultValues: emptyValues });

  const onSubmit = useCallback(
    async (formValues: ProductLineFormValues) => {
      const name = formValues.name.trim();
      const values = cleanProductLineValues(formValues.values);

      if (!name) {
        setError('name', { type: 'required', message: 'Name is required' });
        return;
      }
      if (values.length === 0) {
        setError('values', { type: 'required', message: atLeastOneValueMessage });
        return;
      }

      try {
        const saved = await createProductLine({ name, values }).unwrap();
        toast.success('Product line created');
        navigate(`${productLinesPath}/${saved.id}`);
      } catch (error) {
        console.error('Error creating product line:', error);
        if (!isErrorWithData(error)) {
          toast.error('Failed to create product line. Please try again.');
          return;
        }

        // The typed code says which field clashed, so the server's message lands on that input.
        const { errorCode, message } = error.data;
        if (errorCode === ErrorCode.equipmentDuplicateName) {
          setError('name', { type: 'server', message });
        } else if (errorCode === ErrorCode.equipmentDuplicateAlias) {
          setError('values', { type: 'server', message });
        } else {
          toast.error(message);
        }
      }
    },
    [createProductLine, navigate, setError],
  );

  const handleCancel = useCallback(() => navigate(productLinesPath), [navigate]);

  const pageTitle = 'New Product Line';

  return (
    <DsmPageContainer title={pageTitle}>
      <PageHeader title={pageTitle} eyebrow={<ProductLineBreadcrumb current="New" />} />

      <form onSubmit={handleSubmit(onSubmit)} className="mx-6">
        <div className="mb-16 grid grid-cols-2 gap-x-16 gap-y-8">
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <PalmettoTextInput
                id="product-line-name"
                label="Name"
                helperText="Names can't be changed later."
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                validationMessage={
                  fieldState.error ? { type: 'error' as const, text: fieldState.error.message ?? '' } : undefined
                }
                disabled={isLoading}
              />
            )}
          />
        </div>

        <div className="mb-16">
          <Controller
            name="values"
            control={control}
            render={({ field, fieldState }) => (
              <ProductLineValuesEditor
                value={field.value}
                onChange={field.onChange}
                disabled={isLoading}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>

        <div className="flex justify-end gap-8">
          <PalmettoButton
            type="button"
            emphasis="secondary"
            layout="hug"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </PalmettoButton>
          <PalmettoButton emphasis="primary" layout="hug" disabled={isLoading}>
            Create Product Line
          </PalmettoButton>
        </div>
      </form>
    </DsmPageContainer>
  );
};
